import { Capacitor } from '@capacitor/core';

// Platform detection
export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'ios', 'android', 'web'
export const isIOS = () => getPlatform() === 'ios';
export const isAndroid = () => getPlatform() === 'android';

// IAP Product IDs - must match App Store Connect / Google Play Console
export const IAP_PRODUCTS = {
  monthly: {
    ios: 'us.nutrifitness.premium.monthly',
    android: 'us.nutrifitness.premium.monthly',
    price: 9.99,
  },
  semi_annual: {
    ios: 'us.nutrifitness.premium.semiannual',
    android: 'us.nutrifitness.premium.semiannual',
    price: 49.99,
  },
  annual: {
    ios: 'us.nutrifitness.premium.annual',
    android: 'us.nutrifitness.premium.annual',
    price: 89.99,
  },
};

let SubscriptionsPlugin = null;

// Lazy-load the Subscriptions plugin only on native platforms
const getSubscriptionsPlugin = async () => {
  if (!isNativePlatform()) return null;
  if (SubscriptionsPlugin) return SubscriptionsPlugin;
  try {
    const module = await import('@squareetlabs/capacitor-subscriptions');
    SubscriptionsPlugin = module.Subscriptions;
    return SubscriptionsPlugin;
  } catch (e) {
    console.warn('Subscriptions plugin not available:', e);
    return null;
  }
};

// Fetch available products from the store
export const getStoreProducts = async () => {
  const plugin = await getSubscriptionsPlugin();
  if (!plugin) return [];

  try {
    const platform = getPlatform();
    const productIds = Object.values(IAP_PRODUCTS).map(
      (p) => p[platform] || p.ios
    );
    
    const products = [];
    for (const productId of productIds) {
      try {
        const product = await plugin.getProductDetails({ productIdentifier: productId });
        if (product && product.responseCode === 0) {
          products.push({
            id: productId,
            title: product.title || productId,
            price: product.price || '',
            priceString: product.priceString || product.price || '',
            description: product.description || '',
          });
        }
      } catch (e) {
        console.warn(`Product ${productId} not available:`, e?.message);
      }
    }
    return products;
  } catch (e) {
    console.warn('Store products not yet available:', e?.message);
    return [];
  }
};

// Helper: race a promise against a timeout
const withTimeout = (promise, ms, message) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms))
  ]);
};

// Purchase a subscription
export const purchaseProduct = async (planKey) => {
  const plugin = await getSubscriptionsPlugin();
  if (!plugin) throw new Error('cancelled');

  const platform = getPlatform();
  const product = IAP_PRODUCTS[planKey];
  if (!product) throw new Error('Unknown plan: ' + planKey);

  const productId = product[platform] || product.ios;

  // Pre-check: confirm the product actually exists in the store before calling purchase.
  // This prevents the native plugin from hanging silently (shown as stuck "Processing…")
  // when IAP products are not yet configured in App Store Connect / Google Play Console.
  try {
    const productDetails = await withTimeout(
      plugin.getProductDetails({ productIdentifier: productId }),
      2500,
      'product_lookup_timeout'
    );
    const ok = productDetails && (productDetails.responseCode === 0 || productDetails.data || productDetails.price);
    if (!ok) {
      throw new Error('iap_not_configured');
    }
  } catch (e) {
    if (e?.message === 'iap_not_configured' || e?.message === 'product_lookup_timeout') {
      throw new Error('iap_not_configured');
    }
    // If the lookup itself throws (network/plugin error), fall through to the purchase call
    // and let StoreKit handle it — but with a short timeout so the UI never hangs forever.
  }

  try {
    const result = await withTimeout(
      plugin.purchaseProduct({ productIdentifier: productId }),
      5000,
      'purchase_timeout'
    );
    if (!result || (result.responseCode && result.responseCode !== 0)) {
      throw new Error('iap_not_configured');
    }
    return {
      id: result.transactionId || result.originalTransactionId || Date.now().toString(),
      receipt: result.receipt || null,
      purchaseToken: result.purchaseToken || null,
      ...result,
    };
  } catch (e) {
    const msg = (e?.message || '').toLowerCase();
    if (msg.includes('cancel') || e?.code === 'USER_CANCELLED') {
      throw new Error('cancelled');
    }
    if (msg.includes('timeout') || msg.includes('not_configured') || msg.includes('unavailable')) {
      throw new Error('iap_not_configured');
    }
    throw e;
  }
};

// Finish (acknowledge) a transaction
export const finishTransaction = async (transactionId) => {
  // StoreKit 2 auto-finishes transactions, no action needed
  return;
};

// Restore previous purchases (e.g., after reinstall)
export const restorePurchases = async () => {
  const plugin = await getSubscriptionsPlugin();
  if (!plugin) throw new Error('cancelled');
  try {
    await plugin.restorePurchases();
    return [];
  } catch (e) {
    console.warn('Failed to restore purchases:', e?.message);
    return [];
  }
};
